// React and Redux
import React, { Component } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";

// Libs
import { Col, Row } from "react-bootstrap";
import { Steps, ButtonSpocket } from "shared_components";

// Components
import ModalWrapper from "../../ModalWrapper";
import { default as Loadable } from "../../commonLoadable";

// Actions
import { closeModal } from "../../../../actions/ui";
import { downgradePlan } from "../../../../actions/settings";
import { setAlertMessage } from "../../../../actions";
import { handleUpgradeRequest } from "../../../../actions/upgrade.js";

// Icons
import warningIcon from "../../../../assets/warning-icon.svg";

// Steps
import StepOne from "./StepOne";
import StepTwo from "./StepTwo";
import StepThree from "./StepThree";

// Data
import data from "./data";

// Utils
import { getActivePlan } from "../../../../utils/features";

// Style
import "./style.css";

class DowngradePlanModal extends Component {
  /**
   * Get the event and set it in our cancel_reason or cancel_reason_description state
   * @param  {Object} e synthetic React DOM Event objects
   */
  handleChange = e => {
    if (this.isOtherDescriptionEmpty(e)) {
      this.setState({ cancel_reason: "Other", cancel_reason_description: "" });
    } else if (this.isOtherDescriptionNotEmpty(e)) {
      this.setState({ cancel_reason_description: e.target.value });
    } else {
      this.setState({
        cancel_reason: e.target.value,
        cancel_reason_description: ""
      });
    }
  };

  /**
   * Verify if this.state.cancel_reason is empty
   * @return {Boolean}
   */
  isCancelReasonInvalid() {
    return this.isCancelReasonEmpty() || this.isOtherDescriptionTooShort();
  }

  /**
   * Verify if other reason <TextArea/> is too short
   * @return {Boolean}
   */
  isOtherDescriptionTooShort() {
    return (
      this.state.cancel_reason === "Other" &&
      this.state.cancel_reason_description.length < this.minimumReasonLength
    );
  }

  /**
   * Verify if this.state.cancel_reason is empty
   * @return {Boolean}
   */
  isCancelReasonEmpty() {
    return !this.state.cancel_reason.length;
  }

  /**
   * Verify if other reason <TextArea/> is empty
   * @param  {Object} e synthetic React DOM Event objects
   * @return {Boolean}
   */
  isOtherDescriptionEmpty(e) {
    return e.target.name === "other_description" && e.target.value === "";
  }

  /**
   * Verify if other reason <TextArea/> is not empty
   * @param  {Object} e synthetic React DOM Event objects
   * @return {Boolean}
   */
  isOtherDescriptionNotEmpty(e) {
    return e.target.name === "other_description" && e.target.value;
  }

  /**
   * we need to know if it's the last three reasons
   * because the footer buttons change in this scenario
   * @return {Boolean}
   */
  isLastThreeReasons() {
    return data.lastThreeReasons.find(
      reason => reason.title === this.state.cancel_reason
    );
  }

  /**
   * Finding the right header text for step 3 by cancel_reason
   * @return {String}
   */
  getHeaderTextStepThree() {
    if (this.state.cancel_reason) {
      const reason = data.reasonList.find(
        reason => reason.title === this.state.cancel_reason
      );
      return reason.header;
    }
  }

  /**
   * @return {String}
   */
  getCancelReasonSelected() {
    const { cancel_reason_description, cancel_reason } = this.state;
    return cancel_reason_description
      ? cancel_reason_description
      : cancel_reason;
  }

  upgradeToTier() {
    const { tracker, eventId } = this.props;

    const selectedPlan = this.props.allPlans.find(
      plan => plan.name === "Promotional" && plan.annual === false
    );

    // TODO: Improve this process so we don't have to call after setState
    this.setState(null, () => {
      this.props.handleUpgradeRequest(
        selectedPlan.id,
        selectedPlan.name,
        { ...tracker, event_id: eventId },
        selectedPlan
      );
    });
  }

  state = {
    cancel_reason: "",
    cancel_reason_description: "",
    promotionalReasons: [
      "I cannot afford it",
      "Haven’t made enough sales to justify the cost"
    ]
  };

  // Configuration
  minimumReasonLength = 50;

  steps = [
    {
      title: `Downgrade ${getActivePlan()} Plan`,
      component: () => <StepOne />
    },
    {
      title: "Why do you want to Downgrade?",
      component: cancelReason => (
        <StepTwo
          onChange={this.handleChange}
          cancelReason={cancelReason}
          cancelReasonDescription={this.state.cancel_reason_description}
        />
      )
    },
    {
      component: cancelReason => (
        <StepThree
          cancelReason={cancelReason}
          downgradePlan={() =>
            this.props.downgradePlan(
              this.getCancelReasonSelected(),
              this.props.storeId
            )
          }
        />
      )
    }
  ];

  style = {
    footer: {
      display: "flex",
      alignItems: "center",
      backgroundColor: "#f2f2fa",
      borderRadius: "4px",
      padding: "30px 0"
    },
    button: {
      fontSize: "12px",
      fontWeight: "600",
      height: "36px"
    }
  };

  /*===============================
  =            Renders            =
  ===============================*/
  render() {
    const { cancel_reason, promotionalReasons } = this.state;
    return (
      <ModalWrapper size="Downgrade_Plan_Modal">
        <Loadable active={this.props.isDowngrading || this.props.isUpgrading}>
          <Steps>
            {this.steps.map((step, index) => (
              <Steps.Step key={index}>
                {({
                  currentIndex,
                  currentStep,
                  isLast,
                  nextStep,
                  prevStep
                }) => (
                  <div className="Downgrade_Plan_Modal__container">
                    <ModalWrapper.Header>
                      <img
                        src={warningIcon}
                        alt="Warning icon"
                        style={{ marginRight: "15px" }}
                      />
                      <strong>
                        {currentIndex === 2
                          ? this.getHeaderTextStepThree()
                          : step.title}
                      </strong>
                    </ModalWrapper.Header>

                    <ModalWrapper.Body style={{ display: "inline-block" }}>
                      <Col md={12}>
                        {step.component(this.state.cancel_reason)}
                      </Col>
                    </ModalWrapper.Body>

                    <ModalWrapper.Footer style={this.style.footer}>
                      <Row className="Downgrade_Plan_Modal__footer_container">
                        <Col
                          xs={12}
                          sm={12}
                          md={3}
                          className="Downgrade_Plan_Modal__buttons_left"
                        >
                          {this.renderGoBackButton(currentIndex, prevStep)}
                          <div
                            style={{ fontSize: currentIndex === 0 && "14px" }}
                          >
                            {currentStep}
                          </div>
                        </Col>
                        <Col
                          className="Downgrade_Plan_Modal__buttons_middle"
                          xs={12}
                          sm={12}
                          mdPush={
                            (currentIndex !== 2 ||
                              this.isLastThreeReasons() ||
                              (isLast &&
                                cancel_reason === promotionalReasons[1])) &&
                            2
                          }
                          md={4}
                        >
                          {this.renderCancelButton(isLast)}
                          {this.renderCancelPlanButton(isLast)}
                        </Col>
                        <Col
                          className="Downgrade_Plan_Modal__buttons_right"
                          xs={12}
                          sm={12}
                          md={5}
                        >
                          {/* Inside of each renderButton has a specific rule of when it will appear */}

                          {this.renderContinueButton(
                            nextStep,
                            currentIndex,
                            isLast
                          )}
                          {this.renderSuccessButton(isLast)}
                          {this.renderDowngradeButton(isLast)}
                          {this.props.currentPlan.name !== "Promotional" &&
                            this.renderGetThisOfferButton(isLast)}
                        </Col>
                      </Row>
                    </ModalWrapper.Footer>
                  </div>
                )}
              </Steps.Step>
            ))}
          </Steps>
        </Loadable>
      </ModalWrapper>
    );
  }

  /**
   * Render Go Back Button
   * @param  {Number} currentIndex
   * @param  {Function} prevStep
   * @return {ReactElement}
   */
  renderGoBackButton(currentIndex, prevStep) {
    return (
      <span>
        {currentIndex !== 0 && (
          <div className="Downgrade_Plan_Modal__go_back" onClick={prevStep}>
            Go back
          </div>
        )}
      </span>
    );
  }

  /**
   * Render Cancel Button
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderCancelButton(isLast) {
    const { promotionalReasons, cancel_reason } = this.state;
    const CancelButton = (
      <ButtonSpocket
        customButton={{
          ...this.style.button,
          width: "76px",
          marginRight: "5px",
          zIndex: "2"
        }}
        bsStyle="secondary"
        // bsSize="sm"
        onClick={() => this.props.closeModal()}
      >
        Cancel
      </ButtonSpocket>
    );

    if (!isLast) {
      return CancelButton;
    } else if (
      (isLast && this.isLastThreeReasons()) ||
      (isLast && cancel_reason === promotionalReasons[1])
    ) {
      return CancelButton;
    }
  }

  /**
   * Render Continue Button
   * @param  {Function} nextStep
   * @param  {Number} currentIndex
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderContinueButton(nextStep, currentIndex, isLast) {
    return (
      <span>
        {!isLast && (
          <ButtonSpocket
            customButton={{ ...this.style.button, width: "100px" }}
            bsStyle="warning"
            onClick={e => {
              if (currentIndex === 1 && this.isCancelReasonInvalid()) {
                this.props.setAlertMessage(
                  "Please describe the reason with at least 50 characters.",
                  "error"
                );
              } else {
                nextStep(e);
              }
            }}
            disabled={currentIndex === 1 && this.isCancelReasonEmpty()}
          >
            Continue
          </ButtonSpocket>
        )}
      </span>
    );
  }

  /**
   * Render Get this offer Button
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderGetThisOfferButton(isLast) {
    const { promotionalReasons, cancel_reason } = this.state;
    return (
      <span>
        {isLast &&
          promotionalReasons.includes(cancel_reason) && (
            <ButtonSpocket
              customButton={{
                ...this.style.button
              }}
              bsStyle="success"
              onClick={() => {
                this.upgradeToTier();
              }}
            >
              Get this offer
            </ButtonSpocket>
          )}
      </span>
    );
  }

  /**
   * Render Cancel my plan Button
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderCancelPlanButton(isLast) {
    const { promotionalReasons, cancel_reason } = this.state;
    return (
      <span>
        {isLast &&
          !this.isLastThreeReasons() &&
          cancel_reason !== promotionalReasons[1] && (
            <ButtonSpocket
              customButton={{
                ...this.style.button,
                marginRight: "5px"
              }}
              bsStyle="secondary"
              // bsSize="sm"
              onClick={() =>
                this.props.downgradePlan(
                  this.getCancelReasonSelected(),
                  this.props.storeId
                )
              }
            >
              Cancel my plan
            </ButtonSpocket>
          )}
      </span>
    );
  }

  /**
   * Render Continue Button
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderSuccessButton(isLast) {
    const { promotionalReasons, cancel_reason } = this.state;

    return (
      <span>
        {isLast &&
          !this.isLastThreeReasons() &&
          cancel_reason !== promotionalReasons[1] && (
            <ButtonSpocket
              customButton={{
                ...this.style.button
              }}
              bsStyle="success"
              onClick={() => this.props.closeModal()}
            >
              Yes, continue my plan
            </ButtonSpocket>
          )}
      </span>
    );
  }

  /**
   * Render Downgrade Button
   * @param {Boolean} isLast
   * @return {ReactElement} <ButtonSpocket />
   */
  renderDowngradeButton(isLast) {
    const { promotionalReasons, cancel_reason } = this.state;
    const { currentPlan } = this.props;
    // promotionalReasons.includes(cancel_reason)

    const DowngradeButton = (
      <span>
        <ButtonSpocket
          customButton={{
            ...this.style.button
          }}
          bsStyle="warning"
          // bsSize="sm"
          onClick={() =>
            this.props.downgradePlan(
              this.getCancelReasonSelected(),
              this.props.storeId
            )
          }
        >
          Downgrade
        </ButtonSpocket>
      </span>
    );

    if (
      isLast &&
      [
        "This is temporary, I’ll come back when the time is right",
        "Other"
      ].includes(cancel_reason) &&
      currentPlan.name !== "Promotional"
    ) {
      return DowngradeButton;
    }

    if (
      isLast &&
      [
        "Other",
        "This is temporary, I’ll come back when the time is right",
        ...promotionalReasons
      ].includes(cancel_reason) &&
      currentPlan.name === "Promotional"
    ) {
      return DowngradeButton;
    }
  }
}
function mapStateToProps(state) {
  return {
    isDowngrading: state.settings.isDowngrading,
    storeId: state.settings.storeId,
    allPlans: state.store.plans.allPlans,
    tracker: state.sharedComponents.tracker,
    isUpgrading:
      state.store.planCharges.isFetching ||
      state.store.subscriptions.isFetching,
    currentPlan: state.settings.currentPlan,
    eventId: state.eventTracker.eventId
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    { setAlertMessage, closeModal, downgradePlan, handleUpgradeRequest },
    dispatch
  );
}

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DowngradePlanModal);



// WEBPACK FOOTER //
// ./src/components/_Shared/Modals/DowngradePlanModal/index.js